import currency from 'currency.js';
import { configurationSettings } from '../configuration/configuration-settings';
import { TaxMode } from '../configuration/types/order/tax-mode';
import { isEmptyArray } from './array-utils';
import { areObjectsEquivalent } from './equivalence-utils';
/**
 * Adds any number of [[MonetaryValue]] entities. If no values are provided, returns a [[MonetaryValue]] with a value of 0.
 *
 * @param values The [[MonetaryValue]] entities to be added.
 * @returns The sum of the provided values.
 */
export const addMonetaryValues = (...values) => {
    return values.reduce((total, value) => {
        if (!value)
            return total;
        return {
            valueIncludingTax: currency(total.valueIncludingTax).add(value.valueIncludingTax).value,
            valueExcludingTax: currency(total.valueExcludingTax).add(value.valueExcludingTax).value,
            tax: currency(total.tax).add(value.tax).value
        };
    }, getZeroMonetaryValue());
};
/**
 * Subtracts [[MonetaryValue]] `b` from [[MonetaryValue]] `a`.
 *
 * @param a The [[MonetaryValue]] to be subtracted from.
 * @param b The [[MonetaryValue]] to be subtract.
 * @returns The difference of `a` and `b`.
 */
export const subtractMonetaryValues = (a, b) => ({
    valueIncludingTax: currency(a.valueIncludingTax).subtract(b.valueIncludingTax).value,
    valueExcludingTax: currency(a.valueExcludingTax).subtract(b.valueExcludingTax).value,
    tax: currency(a.tax).subtract(b.tax).value
});
/**
 * Multiplies a [[MonetaryValue]] by a given factor.
 *
 * @param value The [[MonetaryValue]] to be multiplied.
 * @param factor The factor by which to multiply.
 * @returns The resulting [[MonetaryValue]].
 */
export const multiplyMonetaryValue = (value, factor) => ({
    valueIncludingTax: currency(value.valueIncludingTax).multiply(factor).value,
    valueExcludingTax: currency(value.valueExcludingTax).multiply(factor).value,
    tax: currency(value.tax).multiply(factor).value
});
/**
 * Negates a [[MonetaryValue]].
 *
 * @param value The [[MonetaryValue]] to be negated.
 */
export const negateMonetaryValue = (value) => multiplyMonetaryValue(value, -1);
/**
 * Returns a positive value if `a` is greater than `b`, a negative value if `a` is less than `b`, and 0 if `a` is equivalent to `b`.
 */
export const compareMonetaryValue = (a, b) => getValueByTaxMode(a) - getValueByTaxMode(b);
/**
 * Gets the minimum value from an array of [[MonetaryValue]] entities.
 *
 * @param values The [[MonetaryValue]] entities.
 * @returns The minimum [[MonetaryValue]].
 */
export const getMinMonetaryValue = (values) => {
    if (isEmptyArray(values))
        throw new Error('Cannot get minimum monetary value of empty array.');
    return values.reduce((prev, current) => (getValueByTaxMode(current) < getValueByTaxMode(prev) ? current : prev));
};
/**
 * Returns a [[MonetaryValue]] with a value of 0.
 */
export const getZeroMonetaryValue = () => ({ valueIncludingTax: 0, valueExcludingTax: 0, tax: 0 });
/**
 * Returns `true` if the provided [[MonetaryValue]] has a value of 0.
 */
export const isZeroMonetaryValue = (value) => areObjectsEquivalent(value, getZeroMonetaryValue());
const getValueByTaxMode = (value) => {
    const { taxMode } = configurationSettings.data.order;
    return taxMode === TaxMode.Inclusive ? value.valueIncludingTax : value.valueExcludingTax;
};
